import { Button, Card, Row, theme, Tooltip } from 'antd';
import { Eye, EyeOff, Settings } from 'lucide-react';
import moment from 'moment';
import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ENV } from 'src/';
import { AutocompleteTextFilter } from 'src/common/components/table-filters/autocomplete-text-filter';
import { DateRangeFilter } from 'src/common/components/table-filters/date-range-filter';
import { OptionsFilter } from 'src/common/components/table-filters/options-filter';
import { getDateFormat } from 'src/common/utils';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { formatMessage } from 'src/core/services/http.service';
import { Query } from 'src/core/stores/data-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { AppConfig, container } from 'src/inversify.config';
import {
  getNotificationTypeTranslationKey,
  getValidNotificationTypes,
  NotificationDataStore,
  NotificationDataSummary,
} from 'src/stores/notifications-store';
import { NotificationsPreferencesDialog } from './notifications-preferences-dialog';
import { useSessionNotifications } from './notifications-session-store';
import { getNotificationDisplayInformation } from './notifications-utils';
import { useNavigate } from 'react-router';

const { useToken } = theme;

const NotificationsTableView: FC = () => {
  const { t, i18n } = useTranslation();
  const config: AppConfiguration = useContext(AppConfig);
  const navigate = useNavigate();

  const notificationTypesFilter = `(${getValidNotificationTypes()
    .map((v) => `type eq '${v}'`)
    .join(' or ')})`;

  const [query, setQuery] = useState({
    searchQuery: '',
    skip: 0,
    take: 10,
    orderBy: [{ field: 'createdAt', direction: 'Descending' }],
    filter: [{ and: [notificationTypesFilter] }],
  } as Query);

  const inputRef = useRef();
  const notificationsStore = container.get(NotificationDataStore);
  const notificationsState = notificationsStore.state;
  const sessionNotifications = useSessionNotifications();

  const [notificationsPreferencesVisible, setNotificationsPreferencesVisible] = useState(false);
  const { token } = useToken();

  setHeader(t('Notifications'));
  setRoutes([
    { path: '', breadcrumbName: t('Home') },
    { path: 'notifications', breadcrumbName: t('Notifications') },
  ]);

  useEffect(() => {
    notificationsStore.load(query);
  }, [inputRef]); // eslint-disable-line react-hooks/exhaustive-deps

  let tableModel = {
    query: query,
    columns: [
      {
        sortable: true,
        searchable: false,
        field: 'createdAt',
        title: t('Date'),
        filter: <DateRangeFilter />,
        renderer: (data) => <span>{moment(data.createdAt).locale(i18n.language).format(getDateFormat())}</span>,
      },
      {
        sortable: true,
        searchable: false,
        filter: (
          <OptionsFilter
            options={getValidNotificationTypes().map((v) => ({
              label: t(getNotificationTypeTranslationKey(v)),
              value: v,
            }))}
          />
        ),
        field: 'type',
        title: t('Action'),
        renderer: (data) => <span>{t(getNotificationTypeTranslationKey(data.type))}</span>,
      },
      {
        sortable: false,
        searchable: false,
        field: 'description',
        title: t('Description'),
        renderer: (data) => <span>{getNotificationDisplayInformation(data, config.environment === ENV.DEV).description}</span>,
      },
      {
        sortable: true,
        searchable: false,
        field: 'relatedEntityName',
        title: t('AffectedElement'),
        applyPartialFilter: true,
        filter: <AutocompleteTextFilter baseUrl={`${notificationsStore.baseUrl}`} fieldName={'relatedEntityName'} fieldValue={'relatedEntityName'} />,
        renderer: (data) => <span>{data.relatedEntityName}</span>,
      },

      {
        sortable: true,
        searchable: false,
        field: 'redirectEntityName',
        applyPartialFilter: true,
        filter: (
          <AutocompleteTextFilter baseUrl={`${notificationsStore.baseUrl}`} fieldName={'redirectEntityName'} fieldValue={'redirectEntityName'} />
        ),
        title: t('LinkTo'),
        renderer: (data) =>
          getNotificationDisplayInformation(data).path ? (
            <span
              className={'clickable-span strong'}
              onClick={
                getNotificationDisplayInformation(data).path
                  ? () => {
                      const displayInformation = getNotificationDisplayInformation(data);
                      navigate(displayInformation.path!, { state: displayInformation.navigationState });
                    }
                  : undefined
              }
            >
              {data.redirectEntityName}
            </span>
          ) : (
            <></>
          ),
      },
      {
        sortable: true,
        searchable: false,
        field: 'isRead',
        title: t('State'),
        filter: (
          <OptionsFilter
            options={[
              { label: t('Read'), value: true },
              { label: t('Unread'), value: false },
            ]}
          />
        ),
        renderer: (data) => (
          <span>
            {data.isRead ? (
              <Tooltip title={t('Read')}>
                <Eye color={token.colorPrimary} />
              </Tooltip>
            ) : (
              <Tooltip title={t('Unread')}>
                <EyeOff color={token.colorTextDisabled} />
              </Tooltip>
            )}
          </span>
        ),
      },
    ],

    data: notificationsState.value,
  } as TableModel<NotificationDataSummary>;

  return (
    <Row align="top" justify="space-between">
      <Card className={`card-table table-overflow`} style={{ width: '100%' }}>
        <TableView
          searchBarRenderer={() => <></>}
          rightToolbar={
            <li className="buttons-list-element">
              <Button
                className="buttons-list-element"
                type="primary"
                icon={<Settings style={{ color: 'white' }} />}
                onClick={() => setNotificationsPreferencesVisible(true)}
              >
                {t('NotificationsSettings')}
              </Button>
            </li>
          }
          canSort={false}
          rowKey="id"
          canCreateNew
          canDelete={true}
          onDeleteRow={(notification: NotificationDataSummary) => {
            notificationsStore.delete(notification.notificationId);
            if (!notification.isRead) {
              sessionNotifications.deleteUnreadNotification(notification);
            }
          }}
          onQueryChanged={(query: Query) => {
            setQuery(query);
            notificationsStore.load(query);
          }}
          onRefresh={() => notificationsStore.load(query)}
          model={tableModel}
          error={
            notificationsState.errorMessage.value &&
            (config.environment === ENV.DEV ? formatMessage(notificationsState.errorMessage.value) : t('NotificationsTableError'))
          }
        />
      </Card>

      {notificationsPreferencesVisible && <NotificationsPreferencesDialog onClose={() => setNotificationsPreferencesVisible(false)} />}
    </Row>
  );
};

export default NotificationsTableView;
